import React, { useState } from 'react';
import { Document, Page } from 'react-pdf';
import usePinnedPdfWorker from '../../hooks/usePinnedPdfWorker';

const PdfSignatureEditor = ({ pdfData, onSave, onCancel }) => {
    const { ready: pdfWorkerReady, error: pdfWorkerError } = usePinnedPdfWorker();
    const [pdfLoadError, setPdfLoadError] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [fields, setFields] = useState([]); // 配置された印影の座標リスト
    const [activeTool, setActiveTool] = useState(null); // 'sender' | 'recipient'

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    // PDF上のクリックした位置にスタンプを配置
    const handlePageClick = (e, pageNumber) => {
        if (!activeTool) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        const newField = {
            id: Date.now(),
            role: activeTool,
            page_number: pageNumber,
            x_pos: x.toFixed(2),
            y_pos: y.toFixed(2),
            field_type: 'stamp',
            input_value: '' // 初期値は空
        };

        setFields([...fields, newField]);
        setActiveTool(null);
    };

    // 配置済みの印をクリックして名前を入力（自社のみ）
    const handleFieldInput = (id) => {
        const targetField = fields.find(f => f.id === id);
        if (targetField.role !== 'sender') return; // 相手方はエンドが入力するのでここでは無視

        const val = prompt("表示する名前（会社名など）を入力してください", targetField.input_value);
        if (val !== null) {
            setFields(fields.map(f => f.id === id ? { ...f, input_value: val } : f));
        }
    };

    // バリデーション：自社印にすべて名前が入っているか
    const isReadyToSave = fields.length > 0 && fields
        .filter(f => f.role === 'sender')
        .every(f => f.input_value && f.input_value.trim() !== "");

    return (
        <div className="flex h-[85vh] bg-slate-200 rounded-[2.5rem] overflow-hidden border-4 border-white shadow-2xl animate-in zoom-in-95 duration-500">
            
            <div className="w-72 bg-white border-r p-8 flex flex-col shadow-xl z-10">
                <div className="flex-grow">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-6">署名パーツを配置</h4>
                    
                    <div className="space-y-4">
                        <button 
                            onClick={() => setActiveTool('sender')}
                            className={`w-full p-5 rounded-[1.5rem] border-2 transition-all flex flex-col items-center gap-3 ${activeTool === 'sender' ? 'border-red-500 bg-red-50 scale-95 shadow-inner' : 'border-slate-100 hover:border-red-200'}`}
                        >
                            <div className="w-12 h-12 rounded-full border-4 border-red-500 flex items-center justify-center text-red-500 font-black text-[10px] bg-white">自社</div>
                            <span className="text-xs font-black text-slate-700">自社の署名（赤）</span>
                        </button>

                        <button 
                            onClick={() => setActiveTool('recipient')}
                            className={`w-full p-5 rounded-[1.5rem] border-2 transition-all flex flex-col items-center gap-3 ${activeTool === 'recipient' ? 'border-blue-500 bg-blue-50 scale-95 shadow-inner' : 'border-slate-100 hover:border-blue-200'}`}
                        >
                            <div className="w-12 h-12 rounded-full border-4 border-blue-500 flex items-center justify-center text-blue-500 font-black text-[10px] bg-white">相手</div>
                            <span className="text-xs font-black text-slate-700">相手方の署名（青）</span>
                        </button>
                    </div>

                    <div className="mt-8 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                        <p className="text-[10px] text-slate-400 font-bold leading-relaxed">
                            💡 使い方：<br/>
                            1. パーツを選んでPDF上をクリック<br/>
                            2. <span className="text-red-500">赤い自社印</span>をクリックして名前を入力
                        </p>
                    </div>
                </div>

                <div className="space-y-3">
                    <button onClick={onCancel} className="w-full py-3 text-slate-400 font-bold text-sm hover:text-slate-600 transition-colors">中断する</button>
                    <button 
                        onClick={() => onSave(fields)}
                        disabled={!isReadyToSave}
                        className="w-full py-5 bg-slate-900 text-white rounded-full font-black text-sm shadow-xl hover:bg-black disabled:bg-slate-200 disabled:shadow-none transition-all"
                    >
                        {fields.filter(f => f.role === 'sender' && !f.input_value).length > 0 ? '自社印を入力してください' : '配置を確定して次へ'}
                    </button>
                </div>
            </div>

            <div className="flex-grow overflow-y-auto p-4 sm:p-8 lg:p-12 flex flex-col items-center gap-6 sm:gap-8 scrollbar-hide">
                {(pdfWorkerError || pdfLoadError) && (
                    <div className="m-auto rounded-2xl border border-red-100 bg-white p-6 text-sm font-bold text-red-600">
                        {pdfWorkerError || pdfLoadError}
                    </div>
                )}
                {pdfWorkerReady && !pdfLoadError && <Document
                    file={`data:application/pdf;base64,${pdfData}`}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={() => setPdfLoadError('書類を安全に表示できませんでした。')}
                    onSourceError={() => setPdfLoadError('書類を安全に表示できませんでした。')}
                >
                    {Array.from(new Array(numPages), (el, index) => (
                        <div key={`page_${index + 1}`} className="relative shadow-2xl mb-10 transition-transform hover:scale-[1.01]">
                            <Page 
                                pageNumber={index + 1} 
                                width={800}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                onClick={(e) => handlePageClick(e, index + 1)}
                                className="cursor-crosshair bg-white"
                            />
                            
                            {fields.filter(f => f.page_number === index + 1).map(f => (
                                <div
                                    key={f.id}
                                    style={{ left: `${f.x_pos}%`, top: `${f.y_pos}%` }}
                                    onClick={(e) => { e.stopPropagation(); handleFieldInput(f.id); }}
                                    className={`absolute -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full border-4 shadow-xl flex flex-col items-center justify-center cursor-pointer transition-all hover:scale-110 ${f.role === 'sender' ? 'border-red-500 bg-red-50/90 text-red-500' : 'border-blue-500 bg-blue-50/90 text-blue-500'}`}
                                >
                                    {f.input_value ? (
                                        // 入力がある場合は名前だけを表示
                                        <span className="text-[7px] font-black leading-tight text-center px-1 break-all">
                                            {f.input_value}
                                        </span>
                                    ) : (
                                        // 入力がない場合のみラベルを表示
                                        <span className="text-[8px] font-black uppercase">
                                            {f.role === 'sender' ? '自社印' : '相手印'}
                                        </span>
                                    )}

                                    <button 
                                        onClick={(e) => { e.stopPropagation(); setFields(fields.filter(field => field.id !== f.id)); }}
                                        className="absolute -top-2 -right-2 bg-white text-slate-400 rounded-full w-6 h-6 flex items-center justify-center shadow-lg border border-slate-100 hover:text-red-500"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    ))}
                </Document>}
            </div>
        </div>
    );
};

export default PdfSignatureEditor;
